import { CommonModule } from '@angular/common';
import { Component, DEFAULT_CURRENCY_CODE, inject, type OnDestroy } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  tap,
  type Observable,
} from 'rxjs';
import { SubSink } from 'subsink';
import type { CreatePersonalTravelTransaction } from '../../shared/models/api/personal-travel-transaction.model';
import type { RemoteUserTripPurposeDTO } from '../../shared/models/api/remote-user-trip-purpose.model';
import type { ActiveUser } from '../../shared/models/api/user.model';
import type { ControlsOf } from '../../shared/models/controls-of.model';
import { PersonalTravelService } from '../../shared/services/api/personal-travel.service';
import { RemoteUserTripPurposeService } from '../../shared/services/api/remote-user-trip-purpose.service';
import { UserService } from '../../shared/services/api/user.service';
import { OpaValidators } from '../../shared/validators/opa.validators';
import type {
  CreatePersonalTravelData,
  CreatePersonalTravelFormValue,
} from './personal-travel-create.models';

@Component({
  selector: 'opa-personal-travel-create',
  imports: [CommonModule, PorscheDesignSystemModule, ReactiveFormsModule, LetDirective],
  templateUrl: './personal-travel-create.component.html',
  styleUrl: './personal-travel-create.component.scss',
})
export class PersonalTravelCreateComponent implements OnDestroy {
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly remoteUserTripPurposeService = inject(RemoteUserTripPurposeService);
  private readonly userService = inject(UserService);
  private readonly personalTravelService = inject(PersonalTravelService);
  private readonly toastManager = inject(ToastManager);
  private readonly router = inject(Router);
  readonly defaultCurrencyCode = inject(DEFAULT_CURRENCY_CODE);

  private readonly subSink = new SubSink();
  readonly form = this.buildForm();
  readonly textareaMaxLength = 255;
  private readonly submitting = new BehaviorSubject(false);
  templateData$ = this.getTemplateData$();

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  reloadTemplateData(): void {
    this.templateData$ = this.getTemplateData$();
  }

  trackTripPurpose(index: number, tripPurpose: RemoteUserTripPurposeDTO): number {
    return tripPurpose.id;
  }

  trackUser(index: number, user: ActiveUser): number {
    return user.id;
  }

  submit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.next(true);
    const formValue = this.form.getRawValue();
    const personalTravel = this.formValueToPersonalTravel(formValue);
    this.subSink.sink = this.personalTravelService
      .createPersonalTravelTransaction(personalTravel)
      .pipe(
        tap({
          next: () => {
            this.toastManager.addMessage({
              text: `Personal travel transaction created`,
              state: 'success',
            });
            this.router.navigate(['/personal-travel-transactions']);
          },
          error: () => this.submitting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<CreatePersonalTravelFormValue>> {
    const form = this.formBuilder.group<ControlsOf<CreatePersonalTravelFormValue>>({
      tripPurposeId: this.formBuilder.control(null, OpaValidators.required()),
      userId: this.formBuilder.control(null, OpaValidators.required()),
      date: this.formBuilder.control(null, OpaValidators.required()),
      productiveLink: this.formBuilder.control(null, OpaValidators.required()),
      totalExpense: this.formBuilder.control(null, [
        OpaValidators.required(),
        OpaValidators.min(0),
        OpaValidators.maxDecimals(2),
      ]),
      note: this.formBuilder.control(null, [
        OpaValidators.required(),
        OpaValidators.maxLength(this.textareaMaxLength),
      ]),
    });
    return form;
  }

  private formValueToPersonalTravel(
    form: CreatePersonalTravelFormValue,
  ): CreatePersonalTravelTransaction {
    const event: CreatePersonalTravelTransaction = {
      tripPurposeId: Number(form.tripPurposeId!),
      userId: Number(form.userId!),
      date: form.date!,
      productiveLink: form.productiveLink!,
      totalExpense: Number(form.totalExpense!),
      note: form.note!,
    };
    return event;
  }

  private getTemplateData$(): Observable<CreatePersonalTravelData> {
    const tripPurposes$ = this.remoteUserTripPurposeService.getRemoteUserTripPurposes();
    const users$ = this.userService
      .getActiveUsers()
      .pipe(map((users) => users.filter((user) => user.remoteEmployee)));
    const submitting$ = this.submitting.pipe(distinctUntilChanged());
    return combineLatest([tripPurposes$, users$, submitting$]).pipe(
      map(([tripPurposes, users, submitting]) => ({ tripPurposes, users, submitting })),
    );
  }
}
